import { useMemo } from 'react';
import { Input, CustomSelect } from '../../shared';
import { countryRequiresPostalCode } from '../../../utils/countryPostal';

const BUSINESS_TYPES = [
  { value: 'individual', label: 'Individual' },
  { value: 'sole_prop', label: 'Sole proprietorship' },
  { value: 'llc', label: 'LLC' },
  { value: 'corporation', label: 'Corporation' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'other', label: 'Other' },
];

const W9_STATUSES = [
  { value: 'not_collected', label: 'Not collected' },
  { value: 'collected', label: 'Collected' },
  { value: 'not_required', label: 'Not required' },
];

/**
 * Verification step: marketplace tax / legal seller details.
 */
const Step3TaxProfile = ({
  formData,
  errors = {},
  markets = [],
  marketOptions = [],
  handleInputChange,
}) => {
  const taxCountry = (
    formData.tax_residence_country ||
    formData.country ||
    ''
  ).toUpperCase();

  const market = useMemo(() => {
    if (!taxCountry) return null;
    return (markets || []).find(
      (m) => String(m.country_code || '').toUpperCase() === taxCountry
    );
  }, [markets, taxCountry]);

  const required = (market?.required_vendor_fields || []).filter(
    (field) => field !== 'postal_code' || countryRequiresPostalCode(taxCountry)
  );
  const req = (field) => required.includes(field);

  const set = (field, value) => handleInputChange(field, value);

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-50 mb-1">
          Tax profile
        </h3>
        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
          Legal and tax details for marketplace compliance
          {market?.display_name ? ` in ${market.display_name}` : ''}. You can refine this later
          under Tax profile.
        </p>
      </div>

      {required.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Required for this market: {required.join(', ')}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input
          label={req('legal_name') ? 'Legal name *' : 'Legal name'}
          name="legal_name"
          value={formData.legal_name || ''}
          onChange={(e) => set('legal_name', e.target.value)}
          size="sm"
          error={errors.legal_name || ''}
        />
        <Input
          label={req('business_name') ? 'Business name *' : 'Business name'}
          name="tax_business_name"
          value={formData.tax_business_name || formData.business_name || ''}
          onChange={(e) => set('tax_business_name', e.target.value)}
          size="sm"
          error={errors.tax_business_name || ''}
        />
        <CustomSelect
          label={req('business_type') ? 'Business type *' : 'Business type'}
          value={formData.business_type || ''}
          onChange={(value) => set('business_type', value)}
          options={BUSINESS_TYPES}
          placeholder="Select…"
          size="sm"
          error={errors.business_type || ''}
        />
        <CustomSelect
          label={req('tax_residence_country') ? 'Tax residence market *' : 'Tax residence market'}
          value={formData.tax_residence_country || formData.country || ''}
          onChange={(value) => set('tax_residence_country', value)}
          options={marketOptions}
          placeholder="Select market…"
          size="sm"
          error={errors.tax_residence_country || ''}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input
          label={req('address_line1') ? 'Address line 1 *' : 'Address line 1'}
          name="tax_address_line1"
          value={formData.tax_address_line1 || formData.address || ''}
          onChange={(e) => set('tax_address_line1', e.target.value)}
          size="sm"
          error={errors.tax_address_line1 || ''}
        />
        <Input
          label="Address line 2"
          name="tax_address_line2"
          value={formData.tax_address_line2 || ''}
          onChange={(e) => set('tax_address_line2', e.target.value)}
          size="sm"
        />
        <Input
          label={req('city') ? 'City *' : 'City'}
          name="tax_city"
          value={formData.tax_city || formData.city || ''}
          onChange={(e) => set('tax_city', e.target.value)}
          size="sm"
          error={errors.tax_city || ''}
        />
        <Input
          label={req('region_state') ? 'State / province *' : 'State / province'}
          name="tax_region_state"
          value={formData.tax_region_state || formData.state || ''}
          onChange={(e) => set('tax_region_state', e.target.value)}
          size="sm"
          error={errors.tax_region_state || ''}
        />
        <Input
          label={req('postal_code') ? 'Postal code *' : 'Postal code'}
          name="tax_postal_code"
          value={formData.tax_postal_code || formData.zip_code || ''}
          onChange={(e) => set('tax_postal_code', e.target.value)}
          size="sm"
          error={errors.tax_postal_code || ''}
        />
        <Input
          label={req('tax_id') ? 'Tax ID / BN / TIN *' : 'Tax ID / BN / TIN'}
          name="tax_id"
          value={formData.tax_id || ''}
          onChange={(e) => set('tax_id', e.target.value)}
          size="sm"
          error={errors.tax_id || ''}
        />
      </div>

      <div className="space-y-3 border-t border-gray-100 dark:border-gray-800 pt-4">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-50">Registrations</h4>

        <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
          <input
            type="checkbox"
            checked={Boolean(formData.gst_hst_registered)}
            onChange={(e) => set('gst_hst_registered', e.target.checked)}
          />
          GST/HST registered{req('gst_hst_registered') ? ' *' : ''}
        </label>
        {errors.gst_hst_registered && (
          <p className="text-xs text-red-600">{errors.gst_hst_registered}</p>
        )}
        {formData.gst_hst_registered && (
          <Input
            label={req('gst_hst_number') ? 'GST/HST number *' : 'GST/HST number'}
            name="gst_hst_number"
            value={formData.gst_hst_number || ''}
            onChange={(e) => set('gst_hst_number', e.target.value)}
            size="sm"
            error={errors.gst_hst_number || ''}
          />
        )}

        <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
          <input
            type="checkbox"
            checked={Boolean(formData.vat_registered)}
            onChange={(e) => set('vat_registered', e.target.checked)}
          />
          VAT registered{req('vat_registered') ? ' *' : ''}
        </label>
        {formData.vat_registered && (
          <Input
            label={req('vat_number') ? 'VAT number *' : 'VAT number'}
            name="vat_number"
            value={formData.vat_number || ''}
            onChange={(e) => set('vat_number', e.target.value)}
            size="sm"
            error={errors.vat_number || ''}
          />
        )}

        <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
          <input
            type="checkbox"
            checked={Boolean(formData.us_sales_tax_registered)}
            onChange={(e) => set('us_sales_tax_registered', e.target.checked)}
          />
          US sales tax registered{req('us_sales_tax_registered') ? ' *' : ''}
        </label>

        <CustomSelect
          label={req('w9_status') ? 'W-9 status (US) *' : 'W-9 status (US)'}
          value={formData.w9_status || ''}
          onChange={(value) => set('w9_status', value)}
          options={W9_STATUSES}
          placeholder="Not set"
          size="sm"
          error={errors.w9_status || ''}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-gray-100 dark:border-gray-800 pt-4">
        <CustomSelect
          label="Payout market"
          value={formData.payout_country || formData.tax_residence_country || formData.country || ''}
          onChange={(value) => set('payout_country', value)}
          options={marketOptions}
          placeholder="Select…"
          size="sm"
        />
        <Input
          label="Payout currency"
          name="payout_currency"
          value={formData.payout_currency || 'USD'}
          onChange={(e) => set('payout_currency', e.target.value.toUpperCase())}
          maxLength={3}
          size="sm"
        />
      </div>
    </div>
  );
};

export default Step3TaxProfile;
